import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { DateUtilService } from 'gung-common';
import { ConfigBaseFilter, ListLayout, ListLayoutMultipleComponent, SimpleConfigBaseFilter } from 'gung-list';
import { first, forkJoin, map, mergeMap, of } from 'rxjs';
import { OpenOrderTableComponent } from '../../components/open-order-table/open-order-table.component';
import { Order } from '../../models/order';
import { CustomerService } from '../customers/customer.service';
import { MetadataService } from '../metadata/metadata.service';
import { OrderListConfigService } from '../order-list-config/order-list-config.service';
import { OrderService } from '../orders/order.service';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';


@Injectable({
  providedIn: 'root'
})
export class OpenOrdersConfigService extends OrderListConfigService {
  topFilter = true;

  constructor(
    protected translationService: TranslateService,
    protected orderService: OrderService,
    protected selectedCustomerService: SelectedCustomerService,
    protected metadataService: MetadataService,
    protected customerService: CustomerService,
    protected dateUtilService: DateUtilService
  ) {
    super(orderService, selectedCustomerService, dateUtilService);
  }

  getLayouts(): ListLayout<Order>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => OpenOrderTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Order list'
      }
    ];
  }

  getFilters(): ConfigBaseFilter<Order>[] {
    return [new OrderStatusFilter(), new OrderDateFilter(), new OrderDeliveryDateFilter()];
  }

  getSearchTerms(item: Order): string[] {
    const result = super.getSearchTerms(item);

    if (item.extra?.customerName) {
      result.push(item.extra.customerName);
    }

    return result;
  }

  getItems() {
    return this.orderService.getOpenOrders().pipe(
      // Drop entries with a falsy id so the list doesn't break filtering. The backend can
      // emit proxy rows (e.g. DELIVERED_ORDER_PROXY, INVOICED_ORDER_PROXY) with empty ids;
      // filter-list.service throws if getItemId returns falsy. If an order is missing from
      // the list during troubleshooting, check the open-orders payload for items with no id.
      map(orders => orders.filter(order => !!order.id)),
      mergeMap(items => forkJoin([of(items), this.customerService.getCustomers().pipe(first())])),
      map(([orders, customers]) => {
        const result = orders.map(order => {
          return {
            ...order,
            extra: {
              ...order.extra
            }
          };
        });

        result.map(resOrder => {
          const cust = customers.find(customer => customer.id === resOrder.deliveryCustomerId);
          resOrder.extra.customerName = cust ? cust.name : '';
          return resOrder;
        });



        return result;
      })
    );
  }

  getAddQueryParameterLimitToUrl(): boolean {
    return true;
  }
}

class OrderStatusFilter extends SimpleConfigBaseFilter<Order> {
  getName(): string {
    return 'ORDER_STATUS';
  }

  getOptionIds(item: Order): string[] {
    return [item.extra?.gme?.status]
  }
  
  getOptionName(key: string): string {
    return key;
  }
}

export class OrderDateFilter extends SimpleConfigBaseFilter<Order> {
  type = 'dateRangeFilter';

  getName(): string {
    return 'ORDER_DATE';
  }

  getOptionIds(item: Order): string[] {
    if (item?.extra?.gme?.orderDate) {
      return [format(new Date(item?.extra?.gme?.orderDate), 'yyMMdd')];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class OrderDeliveryDateFilter extends SimpleConfigBaseFilter<Order> {
  type = 'dateRangeFilter';

  getName(): string {
    return 'DELIVERY_DATE';
  }

  getOptionIds(item: Order): string[] {
    if (item?.extra?.gme?.deliveryDate) {
      return [format(new Date(item?.extra?.gme?.deliveryDate), 'yyMMdd')];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}